<template>
    <div class="content">
        <Background>
            <FriendlierCard>
                <div class="padded-block">
                    <div class="logo">
                        <LocaleAwareLogo variant="primary" color="cloud" />
                    </div>

                    <!-- Success -->
                    <div v-if="resultState === 'success'" class="result-section">
                        <div class="title">Returns Submitted!</div>
                        <p class="is-cloud description">
                            {{ confirmedCodes.length }} container{{ confirmedCodes.length === 1 ? '' : 's' }} confirmed.
                        </p>
                        <p v-if="amount > 0" class="amount-text">
                            ${{ amount.toFixed(2) }} deposit credited to your account.
                        </p>
                        <ul class="code-list">
                            <li v-for="code in confirmedCodes" :key="code">{{ code }}</li>
                        </ul>
                    </div>

                    <!-- Empty -->
                    <div v-else-if="resultState === 'empty'" class="result-section">
                        <div class="title">No Containers Found</div>
                        <p class="is-cloud description">No container scans were found in the past 24 hours for your account.</p>
                    </div>

                    <!-- Error -->
                    <div v-else-if="resultState === 'error'" class="result-section">
                        <div class="title error-title">Something Went Wrong</div>
                        <p class="is-cloud description">{{ errorMessage }}</p>
                    </div>

                    <div class="button-container">
                        <button class="button primary-button" @click="close">Close</button>
                    </div>
                </div>
            </FriendlierCard>
        </Background>
    </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { AuthService } from "@/revamp/firebase/config";
import { Background, FriendlierCard, LocaleAwareLogo } from "@/revamp/components";

const route = useRoute();
const router = useRouter();

const resultState = ref("");
const confirmedCodes = ref([]);
const amount = ref(0);
const errorMessage = ref("");

onMounted(() => {
    const state = history.state;

    if (!state?.resultState) {
        router.replace({
            name: "BinScan",
            params: { binId: Array.isArray(route.params.binId) ? route.params.binId[0] : route.params.binId },
        });
        return;
    }

    resultState.value = state.resultState;
    confirmedCodes.value = state.confirmedCodes ?? [];
    amount.value = state.amount ?? 0;
    errorMessage.value = state.errorMessage ?? "";
});

function close() {
    router.push(AuthService.currentUser ? { name: "Dashboard" } : { name: "Welcome" });
}
</script>

<style scoped lang="scss">
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";
@import "@/revamp/assets/scss/variables.scss";

.logo {
    max-width: 30%;
    min-width: 100%;
}

.result-section {
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin-top: 15px;

    .title {
        font-size: 1.1rem;
        color: $sunflower;
        width: 100%;
        margin-bottom: 15px;
        text-align: center;
    }

    .error-title {
        color: $red-highlight;
    }

    .description {
        text-align: center;
        margin-bottom: 5px;
    }

    .amount-text {
        color: $green-highlight;
        font-weight: bold;
        text-align: center;
        margin-top: 8px;
        font-size: 1rem;
    }

    .code-list {
        list-style: none;
        padding: 0;
        margin: 12px 0;
        max-height: 35vh;
        overflow-y: auto;
        background-color: white;
        border-radius: 10px;
        -webkit-overflow-scrolling: touch;

        li {
            padding: 14px 16px;
            font-size: 1rem;
            color: black;
            border-bottom: 1px solid $cloud;
            text-align: left;

            &:last-child {
                border-bottom: none;
            }
        }
    }
}

.button-container {
    margin-top: 15px;

    .button {
        width: 100%;
        padding: 18px;
        border-radius: 10px;
        border: none;
        font-weight: bold;
        font-size: 1.1rem;
        cursor: pointer;
        transition: all 0.3s ease;
        touch-action: manipulation;
    }

    .primary-button {
        background-color: $sunflower;
        color: $denim;

        &:active {
            background-color: darken($sunflower, 10%);
        }
    }
}
</style>
